import { useEffect, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useQueryClient } from '@tanstack/react-query'
import { useIdleTimer } from 'react-idle-timer'

import { Queries } from '@/api/queries.enum'
import { clearAuth } from '@/store/slices/authSlice'
import { clearAllTokens, hasValidTokens, getAuthToken } from '@/utils/auth/tokenManager'
import { logger } from '@/utils/logger'
import { NavigationRoutes } from '@/router/navigation.types'
import type { RootState } from '@/store/store'

// Configuration constants - matching Diversio-Frontend patterns
const IDLE_TIMEOUT = 15 * 60 * 1000 // 15 minutes (like Diversio dev mode)
const IDLE_PROMPT_TIMEOUT = 2 * 60 * 1000 // Show warning 2 minutes before logout
const ACTIVITY_THROTTLE = 30 * 1000 // 30 seconds throttle for activity

/**
 * Unified authentication monitoring hook
 * Combines cookie polling, visibility change detection, idle detection, and token refresh
 */
export function useAuthMonitor() {
    const dispatch = useDispatch()
    const queryClient = useQueryClient()
    const { isAuthenticated, isMockAuth } = useSelector((state: RootState) => state.auth)
    const tokenStateRef = useRef({
        access: '',
        refresh: '',
    })
    const isInitialMount = useRef(true)
    const isPromptRef = useRef(false)

    // Update token state ref whenever auth state changes
    useEffect(() => {
        if (isAuthenticated) {
            tokenStateRef.current = {
                access: getAuthToken('access') || '',
                refresh: getAuthToken('refresh') || '',
            }
        }
    }, [isAuthenticated])

    // Activity handler - Just log activity and reset idle state
    const handleActivity = useCallback(() => {
        // Reset prompt flag on activity
        isPromptRef.current = false

        // Just log activity - axios interceptor handles token refresh automatically
        logger.debug('User activity detected')
    }, [])

    // Idle timer handlers
    const handleOnIdle = useCallback(() => {
        logger.warn('User idle timeout reached - logging out')
        clearAllTokens()
        dispatch(clearAuth())
        window.location.href = NavigationRoutes.LOGIN
    }, [dispatch])

    const handleOnActive = useCallback(() => {
        logger.info('User became active after being idle')
        isPromptRef.current = false
    }, [])

    const handleOnPrompt = useCallback(() => {
        logger.warn('User idle warning - showing timeout prompt soon')
        isPromptRef.current = true
        // You could show a modal here warning about impending logout
        // For now, just log it
    }, [])

    // Initialize idle timer - only when authenticated
    const { getRemainingTime, getLastActiveTime } = useIdleTimer({
        timeout: IDLE_TIMEOUT,
        promptBeforeIdle: IDLE_PROMPT_TIMEOUT, // Show warning before logout (updated from deprecated promptTimeout)
        onIdle: handleOnIdle,
        onActive: handleOnActive,
        onPrompt: handleOnPrompt,
        onAction: handleActivity,
        throttle: ACTIVITY_THROTTLE, // Activity throttle
        startOnMount: isAuthenticated,
        startManually: false,
        stopOnIdle: false,
        disabled: !isAuthenticated,
        crossTab: true, // Enable cross-tab idle detection
        events: [
            'mousemove',
            'keydown',
            'wheel',
            'DOMMouseScroll',
            'mousewheel',
            'mousedown',
            'touchstart',
            'touchmove',
            'MSPointerDown',
            'MSPointerMove',
        ],
        // NO immediateEvents - this was causing the logout bug
    })

    useEffect(() => {
        // Skip all monitoring if not authenticated
        if (!isAuthenticated) {
            return
        }

        // Reset initial mount flag when authentication changes
        isInitialMount.current = true

        // 1. Cookie Polling - Check every 1 second for token removal
        const cookiePollInterval = setInterval(() => {
            // Skip cookie polling for mock auth
            if (isMockAuth) {
                return
            }

            // Skip the first few checks to avoid race conditions during initial mount
            if (isInitialMount.current) {
                isInitialMount.current = false
                // Initialize the ref with current token state
                tokenStateRef.current = {
                    access: getAuthToken('access') || '',
                    refresh: getAuthToken('refresh') || '',
                }
                return
            }

            const currentAccessToken = getAuthToken('access')
            const currentRefreshToken = getAuthToken('refresh')
            const previousState = tokenStateRef.current

            // Check if tokens were removed (both previous and current must be truthy/falsy for valid comparison)
            if (previousState.access && !currentAccessToken) {
                logger.info('Access token removed from cookies, logging out', {
                    hadToken: !!previousState.access,
                    hasToken: !!currentAccessToken,
                })
                dispatch(clearAuth())
                window.location.href = NavigationRoutes.LOGIN
            } else if (previousState.refresh && !currentRefreshToken) {
                logger.info('Refresh token removed from cookies, logging out', {
                    hadToken: !!previousState.refresh,
                    hasToken: !!currentRefreshToken,
                })
                dispatch(clearAuth())
                window.location.href = NavigationRoutes.LOGIN
            }

            // Update state ref
            tokenStateRef.current = {
                access: currentAccessToken || '',
                refresh: currentRefreshToken || '',
            }
        }, 1000)

        // 2. Visibility Change Handler - Refetch user data when tab becomes visible
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                // Skip token validation for mock auth
                if (isMockAuth) {
                    logger.info('Tab became visible - mock auth mode, skipping token validation')
                    return
                }

                // Check if tokens still exist
                if (!hasValidTokens()) {
                    logger.info('No valid tokens found on tab focus, logging out')
                    dispatch(clearAuth())
                    // Don't redirect if already on login page
                    if (!window.location.pathname.includes('/auth/')) {
                        window.location.href = NavigationRoutes.LOGIN
                    }
                } else {
                    logger.info('Tab became visible - refetching user data')
                    // Refetch user metadata to validate tokens
                    // This will hit the API due to staleTime: 0 in useUserMetadata
                    queryClient.invalidateQueries({
                        queryKey: [Queries.getUserMetadata],
                    })
                }
            }
        }

        // 3. Storage Event Listener - Cross-tab logout detection (like Diversio-Frontend)
        const handleStorageChange = (e: StorageEvent) => {
            // Skip storage event handling for mock auth
            if (isMockAuth) {
                return
            }

            // Monitor for access token removal in another tab (like Diversio pattern)
            if (e.key === 'optimo_access_token' && e.oldValue && !e.newValue) {
                logger.info('Access token removed in another tab - logging out')
                dispatch(clearAuth())
                window.location.href = NavigationRoutes.LOGIN
            }
            // Also monitor for manual logout signals
            else if (e.key === 'logout' && e.newValue === 'true') {
                logger.info('Logout signal detected in another tab')
                clearAllTokens()
                dispatch(clearAuth())
                window.location.href = NavigationRoutes.LOGIN
            }
        }

        // Add event listeners
        document.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('storage', handleStorageChange)

        // Cleanup
        return () => {
            clearInterval(cookiePollInterval)
            document.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('storage', handleStorageChange)
        }
    }, [dispatch, queryClient, isAuthenticated, isMockAuth])

    // Optional: Log idle time periodically for debugging
    useEffect(() => {
        if (!isAuthenticated) return

        const logInterval = setInterval(() => {
            const remaining = getRemainingTime()
            const lastActive = getLastActiveTime()

            if (remaining < IDLE_PROMPT_TIMEOUT && lastActive && !isPromptRef.current) {
                logger.info(`Idle warning: ${Math.round(remaining / 1000)}s remaining`, {
                    lastActive: new Date(lastActive).toISOString(),
                })
            }
        }, 30000) // Log every 30 seconds when authenticated

        return () => clearInterval(logInterval)
    }, [getRemainingTime, getLastActiveTime, isAuthenticated])
}
